/**
 * Oil & Gas — Operations Dashboard
 *
 * Cross-step KPI summary for the full 8-step intelligence loop.
 * Aggregates live data from corridors, anomalies, missions, and closures
 * to give Integrity Managers a single situational-awareness view.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Flame, AlertTriangle, Navigation, CheckCircle2,
  TrendingUp, RefreshCw, Activity, MapPin, ClipboardCheck,
} from 'lucide-react';
import Button from '../../components/common/Button';
import Spinner from '../../components/common/Spinner';
import Alert from '../../components/common/Alert';
import { KpiCard, StepPill, ActionBanner } from '../../components/common/OperationsDashboard';
import { useNavigate } from 'react-router-dom';
import { anomalyApi, satelliteBoundariesApi } from '../../services/api';
import api from '../../services/api';

interface DashboardData {
  corridors:         number;
  pendingAnomalies:  number;
  criticalAnomalies: number;
  activeOgiSurveys:  number;
  awaitingReview:    number;
  awaitingResponse:  number;
  closedThisMonth:   number;
  totalClosed:       number;
  totalLossBbl:      number;
  plumeDetected:     number;
}

export const OilGasDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData]       = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [anomalyStats, corridorRes, missionRes] = await Promise.all([
        anomalyApi.getStats('oil_gas'),
        satelliteBoundariesApi.getAll(),
        api.get('/missions', { params: { mission_type: 'ogi_survey', limit: 200 } }),
      ]);

      const corridors = (corridorRes.data?.boundaries ?? []).filter(
        (b: any) => b.metadata?.vertical === 'oil_gas',
      ).length;

      const missions: any[] = missionRes.data?.missions ?? missionRes.data?.data?.missions ?? [];
      const oilMissions     = missions.filter((m) => m.metadata?.vertical === 'oil_gas');

      const activeOgiSurveys = oilMissions.filter(
        (m) => m.status === 'in_progress' || m.status === 'pending',
      ).length;

      const awaitingReview = oilMissions.filter(
        (m) => m.metadata?.ai_analysis_status === 'complete' && !m.metadata?.step7_status,
      ).length;

      const awaitingResponse = oilMissions.filter(
        (m) =>
          (m.metadata?.step7_status === 'confirmed_critical' ||
           m.metadata?.step7_status === 'confirmed_monitor') &&
          !m.metadata?.step8_status,
      ).length;

      const closedMissions  = oilMissions.filter((m) => m.metadata?.closure_outcome);
      const thisMonthStart  = new Date(); thisMonthStart.setDate(1); thisMonthStart.setHours(0, 0, 0, 0);
      const closedThisMonth = closedMissions.filter(
        (m) => m.metadata?.closure_sealed_at && new Date(m.metadata.closure_sealed_at) >= thisMonthStart,
      ).length;

      const totalLossBbl = closedMissions.reduce(
        (s: number, m: any) => s + (m.metadata?.closure_loss_volume_bbl ?? 0), 0,
      );

      const plumeDetected = oilMissions.filter((m) => m.metadata?.plume_rate_kg_hr != null).length;

      setData({
        corridors,
        pendingAnomalies:  anomalyStats?.byStatus?.pending    ?? 0,
        criticalAnomalies: anomalyStats?.bySeverity?.critical ?? 0,
        activeOgiSurveys,
        awaitingReview,
        awaitingResponse,
        closedThisMonth,
        totalClosed:       closedMissions.length,
        totalLossBbl,
        plumeDetected,
      });
      setLastRefresh(new Date());
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const actionRequired = (data?.pendingAnomalies ?? 0) + (data?.awaitingReview ?? 0) + (data?.awaitingResponse ?? 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Flame className="w-7 h-7 text-orange-500" /> O&amp;G Operations Dashboard
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Live situational overview across the full 8-step pipeline intelligence loop
          </p>
        </div>
        <div className="flex items-center gap-2">
          <p className="text-xs text-gray-400 dark:text-gray-500">
            Updated {lastRefresh.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
          </p>
          <Button variant="outline" onClick={load} disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {error && <Alert type="error" onClose={() => setError(null)}>{error}</Alert>}

      {!loading && <ActionBanner count={actionRequired} />}

      {loading ? (
        <div className="flex justify-center py-14"><Spinner size="lg" /></div>
      ) : data && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            <KpiCard label="Corridors Monitored" value={data.corridors}         icon={MapPin}        colour="bg-orange-100 dark:bg-orange-900/30 text-orange-600" onClick={() => navigate('/oil-gas/boundaries')} />
            <KpiCard label="Pending Anomalies"   value={data.pendingAnomalies}  icon={AlertTriangle} colour={data.pendingAnomalies > 0 ? 'bg-red-100 dark:bg-red-900/30 text-red-600' : 'bg-gray-100 dark:bg-gray-700 text-gray-500'} onClick={() => navigate('/oil-gas/anomalies')} />
            <KpiCard label="Active OGI Surveys"  value={data.activeOgiSurveys}  icon={Navigation}    colour="bg-blue-100 dark:bg-blue-900/30 text-blue-600"   onClick={() => navigate('/oil-gas/flight-analysis')} />
            <KpiCard label="Closed This Month"   value={data.closedThisMonth}   icon={CheckCircle2}  colour="bg-green-100 dark:bg-green-900/30 text-green-600" onClick={() => navigate('/oil-gas/closure')} />
            <KpiCard label="Plume Surveys"       value={data.plumeDetected}     icon={Activity}      colour="bg-purple-100 dark:bg-purple-900/30 text-purple-600" onClick={() => navigate('/oil-gas/plume-analyzer')} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-3">
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Intelligence Loop — Action Queue</p>
              <StepPill step="1"   label="Corridor Boundaries"   path="/oil-gas/boundaries" />
              <StepPill step="2"   label="Temporal Analysis"      path="/oil-gas/temporal" />
              <StepPill step="3–4" label="Anomaly Alerts"         count={data.pendingAnomalies}  urgent path="/oil-gas/anomalies" />
              <StepPill step="5"   label="OGI Survey Planner"     path="/oil-gas/survey-planner" />
              <StepPill step="6"   label="Flight Data & Analysis" path="/oil-gas/flight-analysis" />
              <StepPill step="7"   label="Analysis Review"        count={data.awaitingReview}    urgent path="/oil-gas/analysis-review" />
              <StepPill step="8a"  label="Response Planning"      count={data.awaitingResponse}  urgent path="/oil-gas/response" />
              <StepPill step="8b"  label="Incident Closure"       path="/oil-gas/closure" />
            </div>

            <div className="space-y-4">
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Summary Metrics</p>

              <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
                <div className="flex items-center justify-between">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Critical Anomalies</p>
                  {data.criticalAnomalies > 0 && <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300">Urgent</span>}
                </div>
                <p className={`text-3xl font-bold mt-1 ${data.criticalAnomalies > 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'}`}>{data.criticalAnomalies}</p>
              </div>

              <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
                <p className="text-xs text-gray-500 dark:text-gray-400">Awaiting Safety Review (Step 7)</p>
                <p className={`text-3xl font-bold mt-1 ${data.awaitingReview > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-gray-900 dark:text-white'}`}>{data.awaitingReview}</p>
              </div>

              <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
                <p className="text-xs text-gray-500 dark:text-gray-400">Total Closed Incidents</p>
                <p className="text-3xl font-bold mt-1 text-green-600 dark:text-green-400">{data.totalClosed}</p>
                {data.totalLossBbl > 0 && <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{data.totalLossBbl} bbl total product loss recorded</p>}
              </div>

              <div className="space-y-2">
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Quick Links</p>
                {[
                  { label: 'Plume & Leak Analyzer',  path: '/oil-gas/plume-analyzer',   icon: Activity },
                  { label: 'Facility Safety',          path: '/oil-gas/facility-safety',  icon: Flame },
                  { label: 'Value Realization & ROI',  path: '/oil-gas/value-realization', icon: TrendingUp },
                  { label: 'Regulatory Reporting',     path: '/oil-gas/regulatory',        icon: ClipboardCheck },
                ].map(({ label, path, icon: Icon }) => (
                  <button key={path} onClick={() => navigate(path)}
                    className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                    <Icon className="w-4 h-4 text-orange-500 flex-shrink-0" />
                    {label}
                    <span className="ml-auto w-3.5 h-3.5 text-gray-400">›</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default OilGasDashboardPage;

// Made with Bob
